import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { PenLine, Save, Eye, MailCheck } from "lucide-react";

// Paste-your-Gmail-signature editor: a rich contentEditable that keeps whatever you paste
// (logo, links, formatting), an on/off switch, and a live preview of exactly what recipients see.
export default function SignatureSettings() {
  const editorRef = useRef(null);
  const [html, setHtml] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/email/signature").then((r) => {
      setHtml(r.data.html || ""); setEnabled(r.data.enabled !== false);
      if (editorRef.current) editorRef.current.innerHTML = r.data.html || "";
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const onInput = () => setHtml(editorRef.current?.innerHTML || "");
  const clear = () => { if (editorRef.current) editorRef.current.innerHTML = ""; setHtml(""); };
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/email/signature", { html, enabled });
      setHtml(r.data.html || "");
      if (editorRef.current) editorRef.current.innerHTML = r.data.html || "";
      toast.success("Signature saved — it'll be added to replies you send from here");
    } catch { toast.error("Could not save your signature"); }
    finally { setSaving(false); }
  };
  const useHouse = async () => {
    setSaving(true);
    try {
      const r = await api.post("/email/signature/house");
      setHtml(r.data.html || ""); setEnabled(true);
      if (editorRef.current) editorRef.current.innerHTML = r.data.html || "";
      toast.success("House signature saved — logo, name, title, phone, email and site");
    } catch { toast.error("Could not build the house signature"); }
    finally { setSaving(false); }
  };
  const sendTest = async () => {
    setTesting(true);
    try {
      const r = await api.post("/email/signature/test");
      toast.success(`Test sent to ${r.data.to} — only you received it. Check your inbox.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send the test email");
    } finally { setTesting(false); }
  };

  return (
    <Card className="p-5 mb-4" data-testid="signature-settings">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
        <div>
          <h3 className="font-semibold text-black flex items-center gap-2"><PenLine className="w-4 h-4 text-[#003E87]" /> Email signature</h3>
          <p className="text-sm text-slate-500 mt-0.5">Open Gmail → your signature, select it all, copy, and paste it right here. Formatting, links and logos come along.</p>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-600 shrink-0">
          <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="signature-enabled" /> {enabled ? "On" : "Off"}
        </label>
      </div>
      <div ref={editorRef} contentEditable suppressContentEditableWarning onInput={onInput}
        data-testid="signature-editor"
        className="min-h-[120px] border border-slate-200 rounded-md p-3 text-sm focus:border-[#003E87] outline-none overflow-auto max-h-72 bg-white"
        style={{ opacity: enabled ? 1 : 0.5 }} />
      <div className="flex items-center gap-2 mt-2">
        <button onClick={clear} data-testid="signature-clear" className="text-xs text-slate-400 hover:text-rose-600">Clear</button>
        <button onClick={useHouse} disabled={saving} data-testid="signature-use-house" className="text-xs text-[#003E87] hover:underline">Use house signature</button>
        <div className="ml-auto flex items-center gap-2">
          <Button onClick={sendTest} disabled={testing || !loaded || !(html || "").trim()} variant="outline" data-testid="signature-test-send"
            title={(html || "").trim() ? "Send yourself a test email — only you receive it" : "Save a signature first"}>
            <MailCheck className="w-4 h-4 mr-1" /> {testing ? "Sending…" : "Send test to myself"}
          </Button>
          <Button onClick={save} disabled={saving || !loaded} data-testid="signature-save" className="bg-[#003E87] hover:bg-[#00306a] text-white">
            <Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : "Save signature"}
          </Button>
        </div>
      </div>
      {(html || "").trim() && (
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center gap-1 mb-1"><Eye className="w-3.5 h-3.5" /> Preview — what recipients see</div>
          <div data-testid="signature-preview" className="border border-slate-100 rounded-md p-3 bg-slate-50 text-sm" dangerouslySetInnerHTML={{ __html: html }} />
        </div>
      )}
    </Card>
  );
}
